import { useParams } from 'react-router-dom';
import { TaskListView } from '@/components/TaskListView.tsx';
import { useProjects } from '@/hooks/useTasks.ts';
import {
  todayFilter,
  todaySort,
  upcomingFilter,
  upcomingSort,
  inboxFilter,
  projectFilter,
  allOpenFilter,
  defaultSort,
} from '@/lib/views.ts';

/**
 * Empty copy rule: say what the screen is for and what to do next. Plain
 * verbs, no apology, no filler. "Inbox is clear." was a status report; it told
 * you nothing about why the screen exists.
 */

export function Today() {
  return (
    <TaskListView
      title="Today"
      subtitle={new Date().toLocaleDateString(undefined, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })}
      filter={todayFilter()}
      sorts={todaySort}
      empty={{
        title: 'Nothing due today.',
        body: 'Anything overdue would show here too. Add work for today above, or look at Upcoming to pull something forward.',
      }}
      captureStatus="todo"
      captureDue="today"
      capturePlaceholder="Add a task for today"
    />
  );
}

export function Upcoming() {
  return (
    <TaskListView
      title="Upcoming"
      subtitle="Everything with a date ahead of today"
      filter={upcomingFilter()}
      sorts={upcomingSort}
      empty={{
        title: 'Nothing scheduled ahead.',
        body: 'Give a task a date and it appears here. Type one — “friday”, “next week” — or use the date chips.',
      }}
      captureStatus="todo"
      captureDue="tomorrow"
      capturePlaceholder="Add a task for later"
    />
  );
}

export function Inbox() {
  return (
    <TaskListView
      title="Inbox"
      subtitle="Captured, not yet sorted"
      filter={inboxFilter()}
      sorts={defaultSort}
      empty={{
        title: 'Inbox is clear.',
        body: 'This is the landing spot for anything you capture without a date or project. Empty is where it should sit.',
      }}
      captureStatus="inbox"
      capturePlaceholder="Capture a thought"
    />
  );
}

export function AllTasks() {
  return (
    <TaskListView
      title="All tasks"
      subtitle="Everything still open, most urgent first"
      filter={allOpenFilter()}
      sorts={defaultSort}
      empty={{
        title: 'No open tasks.',
        body: 'Everything is done or archived. Add the next thing above when it turns up.',
      }}
      captureStatus="inbox"
    />
  );
}

export function ProjectView() {
  const { id } = useParams<{ id: string }>();
  const { data: projects } = useProjects();
  const project = projects?.find((p) => p.id === id);

  if (!id) return null;

  return (
    <TaskListView
      title={project?.name ?? 'Project'}
      subtitle="Everything filed under this project"
      filter={projectFilter(id)}
      sorts={defaultSort}
      empty={{
        title: 'Nothing in this project yet.',
        body: 'Tasks added here are filed to it automatically.',
      }}
      captureStatus="todo"
      captureProjectId={id}
      capturePlaceholder="Add to this project"
      showProject={false}
    />
  );
}
